import React, { useEffect } from 'react';
import '../../App.css';
import '../../tools/search-results/search-results.css';
import './search-catalog-form.css';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// images

// classes
import SearchCatalogResults from './search-catalog-results';
import SearchCatalogForm from './search-catalog-form';
import SelectedItemsTab from './selected-items-tab';
import { TabBar, TabButton } from '../../tools/tab-component/tab-component';
import CollapsingPanel, {
  PanelPosition,
} from '../../tools/collapsing-panel/collapsing-panel';
import { useTranslation } from 'react-i18next';
import { ContextMenu } from '../menus/context-menu';
import ComputeServicesPanel from '../panels/compute-services-panel';

// functions
import { ContainerHook } from '../../utils/functions';

// types
import { ComputeServiceType, DataItemType, JobType, Site } from '../../utils/types';

// enum

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions.
import {
  fnD_buildRucioLocations,
  fnD_getAvailableLocations,
  fnD_getStorageLocationSite,
} from '../../app/data-actions';
import {
  addSearchResults,
  setContextMenuRefreshAddMetadata,
  setSearchCatalogTab,
} from './search-catalog-slice';
import {
  buildQuery,
  getDataProductTypesInResults,
  getTapData,
} from './search-catalog-actions';
import {
  CONTEXT_SYSTEMS,
  CONTEXT_CONTAINERS,
  ctx_setContextRow,
  ctx_setContextVisible,
} from '../menus/context-menu-slice';
import { getRucioSites } from '../data-management/file-browser-actions';
import { setPanelExpanded } from '../../app-slice';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

import { RucioLocationsType } from '../../utils/types';

// types relating to events.
import { Event, EventTarget, EventType, PositionAndFOV } from '../../utils/events';

// types specifically for this component.
import { SelectedRowsType, QueryParameters } from './search-catalog-types';
import { handleEvent } from '../../app-actions';

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function SearchCatalog(props: { dataManagementJobs: JobType[] }) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // GLOBAL STATE VARIABLES:
  const sG_project: { id: number; name?: string } | undefined = useAppSelector(
    (state) => state.global.project,
  );
  const sG_loggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );
  const sNarrowScreen: boolean = useAppSelector((state) => state.global.narrowScreen);

  // DATA STATE VARIABLES:
  const sD_computeServices: ComputeServiceType[] = useAppSelector(
    (state) => state.data.computeServices,
  );

  // rucio locations of selected data.
  const sD_rucioLocations: RucioLocationsType[] = useAppSelector(
    (state) => state.data.rucioLocations,
  ).filter((filter) => filter.catalog.indexOf('search-catalog') > -1);

  // LOCAL STATE VARIABLES:

  // maintain the state for the selected rows on the results tabs.
  const sSelectedRows: SelectedRowsType[] = useAppSelector(
    (state) => state.searchCatalog.selectedItems.selectedRows,
  );

  // search catalog tool:
  const sSearchCatalogTab: string = useAppSelector(
    (state) => state.searchCatalog.searchCatalogTab,
  );
  const sSearchResults: QueryParameters = useAppSelector(
    (state) => state.searchCatalog.searchResults,
  );
  const sDPTRetrieved: boolean = useAppSelector(
    (state) => state.searchCatalog.dptRetrieved,
  );
  const sDPTFilter: string = useAppSelector((state) => state.searchCatalog.dptFilter);
  const sPage: number = useAppSelector((state) => state.searchCatalog.page);
  const sPageSize: number = useAppSelector((state) => state.searchCatalog.pageSize);
  const sDisplayedPage: { dptFilter: string; page: number; pageSize: number } =
    useAppSelector((state) => state.searchCatalog.displayedPage);
  const sContextMenuRefreshAddMetadata: number = useAppSelector(
    (state) => state.searchCatalog.contextMenus.refreshAddMetadata,
  );

  //	-------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	-------------------------------------------------

  // translation function
  const { t } = useTranslation();

  // monitor this container, and if it changes size update the state.
  const dataWindow = ContainerHook({
    container: CONTEXT_CONTAINERS.SEARCH_CATALOG_WHOLE_WINDOW,
  });

  const _selectedItems: DataItemType[] = sD_rucioLocations.map((item) => {
    return {
      namespace: item.namespace,
      filename: item.filename,
    };
  });

  // retrieve the list of sites (in availability order) for the project
  // items and selected project items.
  const _sites: {
    site: Site;
    count: number;
  }[] = getRucioSites({
    allItems: _selectedItems,
    selectedItems: _selectedItems,
    catalog: 'search-catalog',
  });

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	-------------------------------------------------
  //
  //	function that adds a new search results tab when the user clicks on
  //	search on the search parameters form.
  //
  //	-------------------------------------------------

  function addSearchResultsTab(args: { query: QueryParameters }) {
    //
    // replace the current result set with the new query.
    dispatch(addSearchResults(args.query));

    // build the ADQL representation of the query.
    dispatch(buildQuery({ query: args.query }));

    // switch to the results tab.
    dispatch(setSearchCatalogTab('results'));

    // kick off the search.
    dispatch(getTapData({ query: args.query }));

    // get a list of data-product types in this search.
    dispatch(getDataProductTypesInResults({ query: args.query }));

    if (sNarrowScreen) {
      dispatch(setPanelExpanded(false));
    }
    //
  } // addSearchResultsTab

  //	------------------------------------------------------------
  //
  //	Handler for button onClick events.
  //
  //	------------------------------------------------------------

  const onClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    const button: HTMLButtonElement = event.currentTarget;

    // set which tab is selected on the Search Catalog form. If we clicked on a results tab, then store the index of the tab.
    if (button.name === 'btnSelectedItems') {
      dispatch(setSearchCatalogTab('selected-items'));
    }

    // if we clicked on the results tab, switch to it.
    if (button.name === 'btnResults') {
      dispatch(setSearchCatalogTab('results'));
    }
    //
  }; // onClickHandler

  //	------------------------------------------------------------
  //
  //	close the context menus when the user clicks anywhere
  //	in the container.
  //
  //	------------------------------------------------------------

  function onClickDivHandler(event: React.MouseEvent<HTMLDivElement>) {
    //
    dispatch(
      ctx_setContextRow({
        contextSystem: CONTEXT_SYSTEMS.SEARCH_CATALOG_FORM,
        row: -1,
      }),
    );
    dispatch(
      ctx_setContextVisible({
        contextSystem: CONTEXT_SYSTEMS.SEARCH_CATALOG_FORM,
        visible: false,
      }),
    );
    dispatch(setContextMenuRefreshAddMetadata(Date.now()));
    //
  } // onClickDivHandler

  //	-------------------------------------------------
  //
  //	E V E N T S
  //
  //	-------------------------------------------------

  //	--------------------------------------------------------------------------
  //
  //	H O O K S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	build the list of Rucio locations for the selected files.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    console.log('[sSelectedRows hook fires [search-catalog]');

    fnD_buildRucioLocations({
      items: sSelectedRows.map((item) => {
        return {
          namespace: item.namespace,
          filename: item.filename,
        };
      }),
      catalog: 'search-catalog',
    });
    //
  }, [sSelectedRows]);

  //	------------------------------------------------------------
  //
  //	re-run the search-catalog query if the user has changed
  //	the data-product type filter.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    let reRunQuery: boolean = false;
    if (sDisplayedPage !== undefined) {
      //
      // has the filter, page, or page size on the current tab changed?
      reRunQuery =
        reRunQuery ||
        sDisplayedPage.dptFilter !== sDPTFilter ||
        sDisplayedPage.page !== sPage ||
        sDisplayedPage.pageSize !== sPageSize;
      //
    }

    // re-run the query.
    if (reRunQuery === true && sDPTRetrieved) {
      dispatch(getTapData({ query: sSearchResults }));
    }
    //
  }, [sDPTFilter, sPage, sPageSize]);

  //	-------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	-------------------------------------------------

  return (
    <div
      ref={dataWindow}
      id="search-catalog-whole-window"
      className="search-catalog-layout"
      onClick={onClickDivHandler}
    >
      <CollapsingPanel
        className="br-d-r"
        position={PanelPosition.LEFT}
        title={t('Filter')}
        height="100%"
        width="280px"
      >
        <SearchCatalogForm
          addSearchResultsTab={addSearchResultsTab}
          project={sG_project}
          hideHeader={true}
        />
      </CollapsingPanel>

      <div className="search-catalog-main">
        {/*
        ///  the query results panel
        */}
        <div
          role="region"
          aria-label="Search results"
          className="panel-main" // flex-grow-1 min-h-0 d-flex flex-column"
          style={{
            flex: '3 3',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            zIndex: 0,
          }}
        >
          {/*
          ///  header row containing Results tab and Selected items tab
          */}
          <TabBar aria-label={t('Search results tabs')} style={{ flex: '0 0 auto' }}>
            <div style={{ flex: '0 0 8px' }} />
            <TabButton
              index={0}
              text={t('Results')}
              onClick={() => {
                if (!sDPTRetrieved) {
                  return;
                }
                dispatch(setSearchCatalogTab('results'));
              }}
              onClose={undefined}
              selected={sDPTRetrieved && sSearchCatalogTab === 'results'}
              disabled={!sDPTRetrieved}
            />
            <TabButton
              index={1}
              text={t('Selected items')}
              number={sD_rucioLocations.length}
              onClick={() => {
                if (!sDPTRetrieved) {
                  return;
                }
                dispatch(setSearchCatalogTab('selected-items'));
              }}
              onClose={undefined}
              selected={sSearchCatalogTab === 'selected-items'}
              disabled={!sDPTRetrieved}
            />
            <div style={{ flex: '0 0 8px' }} />
          </TabBar>

          {/*
          ///  display the single results and the selected-items
          */}
          <div
            className="flex-grow-1"
            style={{
              minHeight: 0,
            }}
          >
            {sDPTRetrieved && (
              <SearchCatalogResults
                key={'results'}
                query={sSearchResults}
                hidden={sSearchCatalogTab !== 'results'}
              />
            )}
            <SelectedItemsTab hidden={sSearchCatalogTab !== 'selected-items'} />
          </div>
        </div>

        {/*
        ///  the compute services panel
        */}
        {sSearchCatalogTab === 'selected-items' && sG_loggedIn === true && (
          <ComputeServicesPanel
            sites={_sites.length > 0 ? [_sites[0].site] : []}
            flex="0 0 auto"
            stagingCatalog="search-catalog"
          />
        )}
      </div>
      <ContextMenu
        key={'form_' + sContextMenuRefreshAddMetadata.toString()}
        system={CONTEXT_SYSTEMS.SEARCH_CATALOG_FORM}
      />
    </div>
  );
  //
} // SearchCatalog
